import React, { useEffect, useState, Suspense } from "react";
import { Layout, Spin } from "antd";

import SubscribeModal from "components/organisms/SubscribeModal";
import HowToDrawer from "components/support/HowToDrawer";
import Header from "components/layout/Header";
import Sidebar from "components/layout/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "reducers/layoutSlice";
import { getBookmarks } from "reducers/bookmarksSlice";
import { useLocation } from "react-router-dom";
import { isAuthenticated } from "api";
import { trackEvent } from "api/analytics";
import { loadSubscription, loadPlans, loadProducts } from "reducers/layoutSlice";
import useMediaQuery from "hooks/useMediaQuery";
import { ASK_NARROW } from "pages/ask/askBreakpoints";
import { AskShellProvider } from "pages/ask/AskShellContext";

export default function SiderLayout({ children, option, title, beta, isPublic, style = {} }) {
    const { sidebarCollapsed, plans, products } = useSelector(({ layout }) => layout);
    const { bookmarks } = useSelector(({ bookmarks }) => bookmarks);
    const showDrawOnMap = useSelector(({ layout }) => layout.showDrawOnMap);
    const user = useSelector(({ layout }) => layout.user);
    const dispatch = useDispatch();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const alert_id = query.get("alert_id");
    const signal_id = query.get("signal_id");

    const isAsk = location.pathname === "/ask" || location.pathname.startsWith("/ask/");
    const isNarrow = useMediaQuery(ASK_NARROW);
    const [appNavOpen, setAppNavOpen] = useState(false);

    /*
     * Leaving Ask, or growing past the compact breakpoint, must close the overlay -- otherwise
     * the next page would inherit an open menu that belongs to a shell it does not use.
     */
    useEffect(() => {
        setAppNavOpen(false);
    }, [location.pathname, isNarrow]);

    useEffect(() => {
        if (isAuthenticated() && !plans && !plans?.loading) {
            dispatch(loadSubscription());
            dispatch(loadPlans());
        }
    }, [plans, dispatch]);

    // Public pages (e.g. /optm/login) render this layout signed out; these calls would bounce them to login.
    useEffect(() => {
        if (isAuthenticated() && !bookmarks && !bookmarks?.loading) {
            dispatch(getBookmarks());
        }
    }, [bookmarks, dispatch]);

    useEffect(() => {
        if (isAuthenticated() && !products && !products?.loading) {
            dispatch(loadProducts());
        }
    }, [products]);

    useEffect(() => {
        if (alert_id || signal_id) {
            trackEvent("Account Mgmt - Notification Opened", {
                "alert id": query.get("alert_id") || "",
                "signal id": query.get("signal_id") || "",
            });
        }
    }, [alert_id, signal_id]);

    useEffect(() => {
        if (!isPublic) {
            if (!user) {
                dispatch(loadUser());
            }
        }
    }, [dispatch, user, isPublic]);

    // Derived in render, not in an effect: the map resizes itself in an effect on
    // sidebarCollapsed, and that has to see these classes (and the height they set) already.
    const classList = ["main-layout"];
    if (isPublic) {
        classList.push("collapsed-sider");
    } else {
        sidebarCollapsed && classList.push("collapsed-sider");
        // Ask owns its own title in the rail and the conversation header; no app Header above it.
        !showDrawOnMap && !isAsk && classList.push("main-layout--has-header");
    }
    if (isAsk) classList.push("main-layout--ask");
    const classes = classList.join(" ");

    useEffect(() => {
        document.title = `Arbo - ${title}`;
    }, [title]);

    const shell = (
        <>
            <Sidebar
                option={option}
                isPublic={isPublic}
                askOverlay={isAsk && isNarrow}
                onNavigate={() => setAppNavOpen(false)}
            />
            {isAsk && isNarrow && appNavOpen ? (
                <button
                    type="button"
                    className="ask-app-nav-backdrop"
                    aria-label="Close app menu"
                    onClick={() => setAppNavOpen(false)}
                />
            ) : null}
            <HowToDrawer />
            <Layout style={style} className={classes}>
                {!showDrawOnMap && !isAsk ? <Header title={title} beta={beta} /> : null}
                <Suspense fallback={<Spin />}>{children}</Suspense>
            </Layout>
            <SubscribeModal />
        </>
    );

    const outerClass = [isAsk ? "ask-shell" : "", isAsk && isNarrow && appNavOpen ? "ask-shell--nav-open" : ""]
        .filter(Boolean)
        .join(" ");

    return (
        <Layout hasSider={true} className={outerClass || undefined}>
            {isAsk ? (
                <AskShellProvider
                    appNavOpen={appNavOpen}
                    onAppNavOpen={() => setAppNavOpen(true)}
                    onAppNavClose={() => setAppNavOpen(false)}
                >
                    {shell}
                </AskShellProvider>
            ) : (
                shell
            )}
        </Layout>
    );
}
